import { CalendarAccessStatus, CalendarConnectionStatus } from '@app/domain/one_to_one/model/calendar-connected.model';
import { CompanyFeatures } from '@app/models/company-features.model';
import { SSOProvider } from '@app/models/auth/sso-provider.enum';
import { Globals } from '@app/shared/globals/globals';

// What Settings > Notifications can offer for the user's calendar. Shared by that page and the calendar banner on the
// 1:1 pages, so the banner only sends people to Settings when there is something there to click.

// Which company feature enables signing in with each provider. A Record over the enum, so a new provider fails to
// compile until it is given one.
const SSO_FEATURES: Record<SSOProvider, CompanyFeatures> = {
  [SSOProvider.MICROSOFT]: CompanyFeatures.SSO_MICROSOFT,
  [SSOProvider.GOOGLE]: CompanyFeatures.SSO_GOOGLE
};

// The order the Connect buttons show in, the same as the login page's sign-in buttons.
const CONNECT_ORDER: SSOProvider[] = [SSOProvider.MICROSOFT, SSOProvider.GOOGLE];

/** Whether the company signs in with the provider: the parent SSO feature and the provider's own are both on. */
export function companySignsInWith(globals: Globals, provider: SSOProvider): boolean {
  return globals.hasFeature(CompanyFeatures.SSO) && globals.hasFeature(SSO_FEATURES[provider]);
}

/**
 * Whether a calendar that lost access can be reconnected: always, with its own provider, either by signing in again or
 * by connecting it again (see below). Not for NOT_LINKED or UNAVAILABLE, which connecting again wouldn't fix.
 */
export function canReconnect(status: CalendarConnectionStatus): boolean {
  return status.access === CalendarAccessStatus.ACCESS_LOST && !!status.provider;
}

/**
 * Whether a lost calendar is reconnected by signing in with its provider again: while the company still signs in with
 * that provider (or the backend refuses). Never another provider: if the company turned the lost one off, the user now
 * signs in with a password, and a sign-in with a different provider would store a token that makes sign-in
 * enforcement refuse that password.
 */
export function reconnectsBySignIn(status: CalendarConnectionStatus, globals: Globals): boolean {
  return canReconnect(status) && companySignsInWith(globals, status.provider);
}

/** Otherwise a lost calendar is connected again: one connected on Settings, which no sign-in can restore. */
export function reconnectsByConnecting(status: CalendarConnectionStatus, globals: Globals): boolean {
  return canReconnect(status) && !companySignsInWith(globals, status.provider);
}

/**
 * The providers a calendar can be connected with when none is linked. Only those the company doesn't sign in with:
 * where it does, its sign-in already links the calendar, and connecting one would store a token that makes sign-in
 * enforcement refuse the password of a user who signs in with one. A linked calendar that lost access is reconnected
 * instead.
 */
export function connectProviders(status: CalendarConnectionStatus, globals: Globals): SSOProvider[] {
  if (status.access !== CalendarAccessStatus.NOT_LINKED) {
    return [];
  }
  return CONNECT_ORDER.filter(p => !companySignsInWith(globals, p));
}

/**
 * Why the user should go to Settings about their calendar, or null when there's nothing there for them to do: the
 * calendar lost access and can be reconnected (ACCESS_LOST), or none is linked and one can be connected (NOT_LINKED).
 * Never for UNAVAILABLE, which connecting again wouldn't fix, and never when Settings offers no button for it.
 */
export function calendarNeedsAction(status: CalendarConnectionStatus, globals: Globals): CalendarAccessStatus | null {
  if (!status) {
    return null;
  }
  if (canReconnect(status)) {
    return CalendarAccessStatus.ACCESS_LOST;
  }
  if (connectProviders(status, globals).length > 0) {
    return CalendarAccessStatus.NOT_LINKED;
  }
  return null;
}
